import { useCallback, useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useCms } from '@/hooks/useCms';
import { slugify } from '@/utils/slugify';
import { setPageTitle } from '@/lib/appInsights';

import type { article } from '@/types/articles';
import type { CMSContextType } from '@/types/util.types';
import type { category } from '@/types/category';

/**
 * Custom hook that manages the data fetching and state for the Category page.
 * It handles retrieving category information, fetching articles for the category,
 * loading featured articles, and managing pagination.
 *
 * @returns Object containing category data, articles, loading states, and data fetching functions
 */
export function useCategoryPageData() {
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { categories, getArticlesByCategory } = useCms() as CMSContextType;
  const [articles, setArticles] = useState<article[]>();
  const [category, setCategory] = useState<category | null>();
  const [articlesLoading, setArticlesLoading] = useState<boolean>(true);
  const [featuredArticleLoading, setFeaturedArticleLoading] = useState<boolean>(true);
  const [featuredArticle, setFeaturedArticle] = useState<article | null>();
  const [pagination, setPagination] = useState({ page: 1, pageCount: 1 });

  const currentPage = parseInt(searchParams.get('page') || '1', 10);

  const getCategory = useCallback(() => {
    if (categories) {
      const foundCategory = categories.find((category) => slugify(category.name) === params.category);
      return foundCategory;
    }
  }, [params.category, categories]);

  const fetchArticles = useCallback(
    async (pageNumber = currentPage) => {
      setArticlesLoading(true);
      const category = getCategory();

      if (category) {
        const { subCategory, contentLabel } = params;

        const content = await getArticlesByCategory(category.id, pageNumber, {
          subCategory,
          contentLabel,
        });

        setArticles(content.articles);
        setPagination({
          page: content.pagination.page,
          pageCount: content.pagination.pageCount,
        });

        setSearchParams((previousParams) => {
          if (pageNumber === 1) {
            previousParams.delete('page');
          } else {
            previousParams.set('page', pageNumber.toString());
          }
          return previousParams;
        });
      }

      setArticlesLoading(false);
    },
    [params.subCategory, params.contentLabel, params.category, categories, currentPage, setSearchParams]
  );

  const fetchFeaturedArticle = useCallback(async () => {
    setFeaturedArticleLoading(true);
    const category = getCategory();

    if (category) {
      const content = await getArticlesByCategory(category.id, 1);
      setFeaturedArticle(content.articles[0]);
    }

    setFeaturedArticleLoading(false);
  }, [params.category, categories]);

  useEffect(() => {
    fetchArticles(currentPage);
  }, [params.subCategory, params.category, params.contentLabel, categories]);

  useEffect(() => {
    const category = getCategory();
    setCategory(category);

    if (category) {
      setPageTitle(category.name);
    }

    fetchFeaturedArticle();
  }, [params.category, categories]);

  return {
    category,
    articles,
    articlesLoading,
    featuredArticle,
    featuredArticleLoading,
    pagination,
    fetchArticles,
  };
}
